import { useQuery } from "@tanstack/react-query";
import { ShieldCheckIcon, ShieldWarningIcon, SpinnerGapIcon, ArrowSquareOutIcon, WarningIcon, CheckCircleIcon, XCircleIcon } from "@phosphor-icons/react";
import { fetchRugcheck } from "../lib/api";
import { RuggedButton, CreatorRugBadge, AddAlphaButton } from "./RugControls";

const Chip = ({ ok, label, value }) => (
  <div className="flex items-center justify-between border border-white/10 px-3 py-2">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className={`flex items-center gap-1.5 font-mono text-xs font-semibold ${ok ? "text-neon" : "text-danger"}`}>
      {ok ? <CheckCircleIcon size={14} weight="fill" /> : <XCircleIcon size={14} weight="fill" />}
      {value}
    </span>
  </div>
);

const concColor = (pct) => (pct >= 50 ? "#FF3366" : pct >= 30 ? "#F5A623" : "#00E599");

export const RugForensics = ({ address }) => {
  const { data, isLoading } = useQuery({
    queryKey: ["rugcheck", address],
    queryFn: () => fetchRugcheck(address),
    staleTime: 300000,
  });

  return (
    <div data-testid="rug-forensics-panel" className="border border-white/10 bg-card p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-bold text-lg flex items-center gap-2">
          <ShieldCheckIcon size={20} className="text-neon" /> Dev / Rug Forensics
        </h2>
        {data?.available && (
          <a data-testid="rugcheck-link" href={data.rugcheckUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[11px] font-mono text-muted-foreground hover:text-neon transition-colors">
            RugCheck <ArrowSquareOutIcon size={12} />
          </a>
        )}
      </div>

      {data?.available && (
        <div className="flex items-center justify-between mb-4 border border-white/10 p-3">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Your rug ledger</span>
            <CreatorRugBadge creator={data.creator} />
            {!data.creator && <span className="text-[11px] text-muted-foreground font-mono">creator not identified</span>}
          </div>
          <div className="flex items-center gap-2">
            <AddAlphaButton creator={data.creator} withLabel size={16} />
            <RuggedButton mint={address} symbol={data.symbol} creator={data.creator} withLabel size={16} />
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-6 justify-center">
          <SpinnerGapIcon size={20} className="animate-spin text-neon" />
          <span className="font-mono text-xs">Scanning contract…</span>
        </div>
      ) : !data?.available ? (
        <p className="font-mono text-xs text-muted-foreground py-4">On-chain forensics unavailable for this token right now.</p>
      ) : (
        <div className="space-y-3">
          {data.rugged && (
            <div className="flex items-center gap-2 border border-danger/40 bg-danger/10 px-3 py-2 text-danger text-sm font-semibold">
              <ShieldWarningIcon size={18} weight="fill" /> Flagged as RUGGED
            </div>
          )}

          {typeof data.riskScore === "number" && (
            <div className="flex items-center justify-between border border-white/10 px-3 py-2">
              <span className="text-xs text-muted-foreground">RugCheck risk score</span>
              <span className="font-mono text-sm font-bold" style={{ color: concColor(data.riskScore) }}>
                {data.riskScore}/100 <span className="text-[10px] text-muted-foreground font-normal">lower = safer</span>
              </span>
            </div>
          )}

          {data.bundle && (
            <div data-testid="bundle-indicator" className="flex items-center justify-between border px-3 py-2.5"
              style={{
                borderColor: data.bundle.detected ? (data.bundle.severity === "high" ? "rgba(255,51,102,0.45)" : "rgba(245,166,35,0.45)") : "rgba(0,229,153,0.35)",
                background: data.bundle.detected ? (data.bundle.severity === "high" ? "rgba(255,51,102,0.08)" : "rgba(245,166,35,0.07)") : "rgba(0,229,153,0.06)",
              }}>
              <div className="flex items-center gap-2">
                <span className="text-base">📦</span>
                <div>
                  <div className="text-xs font-semibold" style={{ color: data.bundle.detected ? (data.bundle.severity === "high" ? "#FF3366" : "#F5A623") : "#00E599" }}>
                    {data.bundle.detected ? (data.bundle.severity === "high" ? "Likely BUNDLED — high" : "Possible bundle / insiders") : "No bundle detected"}
                  </div>
                  <div className="text-[10px] text-muted-foreground font-mono">
                    {data.bundle.detected
                      ? `${data.bundle.insiderWallets} coordinated wallets · ${data.bundle.networks} cluster${data.bundle.networks === 1 ? "" : "s"}`
                      : "no coordinated insider wallets found"}
                  </div>
                </div>
              </div>
              {data.bundle.heldPct > 0 && (
                <span className="font-mono text-sm font-bold" style={{ color: concColor(data.bundle.heldPct) }} title="Estimated supply held by insider/bundled wallets">
                  {data.bundle.heldPct}%
                </span>
              )}
            </div>
          )}

          {data.lpUnlocked && (
            <div data-testid="lp-unlocked-indicator" className="flex items-center gap-2 border px-3 py-2.5"
              style={{ borderColor: "rgba(255,51,102,0.5)", background: "rgba(255,51,102,0.08)" }}>
              <span className="text-base">🔓</span>
              <div>
                <div className="text-xs font-bold" style={{ color: "#FF3366" }}>LP UNLOCKED — BEWARE</div>
                <div className="text-[10px] text-muted-foreground font-mono">
                  Liquidity isn't locked/burned — the dev can pull it at any time. Real demand but fragile; take profits fast.
                </div>
              </div>
            </div>
          )}

          {data.lpLocked && (
            <div data-testid="lp-locked-indicator" className="flex items-center gap-2 border px-3 py-2.5"
              style={{ borderColor: "rgba(74,222,128,0.5)", background: "rgba(74,222,128,0.08)" }}>
              <span className="text-base">🔒</span>
              <div>
                <div className="text-xs font-bold" style={{ color: "#4ADE80" }}>LP LOCKED / BURNED{data.lpLockedPct ? ` (${data.lpLockedPct}%)` : ""}</div>
                <div className="text-[10px] text-muted-foreground font-mono">
                  Liquidity is locked or burned — the dev can't pull it. One less rug vector (still DYOR on holders & authorities).
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-2">
            <Chip ok={data.mintRevoked} label="Mint authority" value={data.mintRevoked ? "Revoked" : "Active"} />
            <Chip ok={data.freezeRevoked} label="Freeze authority" value={data.freezeRevoked ? "Revoked" : "Active"} />
            <Chip ok={!data.mutableMetadata} label="Metadata" value={data.mutableMetadata ? "Mutable" : "Immutable"} />
            {data.devSold !== null && (
              <Chip ok={data.devSold} label="Dev holdings" value={data.devSold ? "Dev sold / 0%" : `Dev holds ${data.creatorPct}%`} />
            )}
          </div>

          <div className="grid grid-cols-3 gap-2 text-center border-t border-white/5 pt-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Top 10</div>
              <div className="font-mono font-bold text-sm mt-0.5" style={{ color: concColor(data.top10Pct) }}>{data.top10Pct}%</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Top holder</div>
              <div className="font-mono font-bold text-sm mt-0.5" style={{ color: concColor(data.topHolderPct) }}>{data.topHolderPct}%</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Insiders</div>
              <div className="font-mono font-bold text-sm mt-0.5" style={{ color: concColor(data.insidersPct) }}>{data.insidersPct}%</div>
            </div>
          </div>

          {data.risks?.length > 0 && (
            <div className="border-t border-white/5 pt-3 space-y-1.5">
              {data.risks.slice(0, 5).map((r, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <WarningIcon size={13} weight="fill" className="shrink-0 mt-0.5" style={{ color: r.level === "danger" ? "#FF3366" : "#F5A623" }} />
                  <span className="text-muted-foreground"><span className="text-white font-medium">{r.name}</span>{r.description ? ` — ${r.description}` : ""}</span>
                </div>
              ))}
            </div>
          )}

          <p className="text-[10px] text-muted-foreground leading-relaxed pt-1">Automated on-chain checks via RugCheck. Research aid, not financial advice — always DYOR.</p>
        </div>
      )}
    </div>
  );
};
